'use client';

import React, { useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/cn';
import type { Owner } from '@repo/api';
import { Avatar, Rating, Tag, BottomSheet, InfoPopover } from '@/components/ui';
import { isTopRated, titleCase } from '@repo/utils';
import { contactUrl } from '@/utils/routes';
import { IconTopRated, IconWorst } from '@repo/svgs';
import { useDeviceType } from '@/hooks/useDeviceType';

export interface OwnerCardProps {
  /** Core owner data from API */
  owner: Owner;
  /** Optional className to customize container */
  className?: string;
}

function OwnerHighlightsContent() {
  return (
    <div className={cn('flex flex-col gap-1')}>
      <div className={cn('flex gap-4 items-start px-2 py-3')}>
        <div className={cn('w-6 h-6 flex-shrink-0')}>
          <IconTopRated className="w-full h-full" />
        </div>

        <div className={cn('flex-1 flex flex-col gap-1')}>
          <p className={cn('text-body-2-high text-primary')}>Top rated owner or associate</p>
          <p className={cn('text-caption text-secondary')}>
            Associated buildings are highly reviewed and rated 4+ stars by past and current renters.
          </p>
        </div>
      </div>
    </div>
  );
}

function WorstLandlordHighlightsContent() {
  return (
    <div className={cn('flex flex-col gap-1')}>
      <div className={cn('flex gap-4 items-start px-2 py-3')}>
        <div className={cn('w-6 h-6 flex-shrink-0')}>
          <IconWorst className="w-full h-full text-icon-worst" />
        </div>

        <div className={cn('flex-1 flex flex-col gap-1')}>
          <p className={cn('text-body-2-high text-primary')}>Worst landlord</p>
          <p className={cn('text-caption text-secondary')}>
            This owner or associate is ranked in the top 100 worst landlords in NYC.
          </p>
        </div>
      </div>
    </div>
  );
}

export function OwnerCard({ owner, className, ...props }: OwnerCardProps) {
  const router = useRouter();
  const { isMobile } = useDeviceType();
  const [showTagDetails, setShowTagDetails] = useState(false);
  const [activeTag, setActiveTag] = useState<'topRated' | 'worstLandlord' | null>(null);
  const topRatedTagRef = useRef<HTMLDivElement | null>(null);
  const worstLandlordTagRef = useRef<HTMLDivElement | null>(null);

  const hasReviews =
    typeof owner.averageRating === 'number' && typeof owner.reviewCount === 'number';
  const isOwnerTopRated = isTopRated(owner.averageRating, owner.reviewCount);
  const isWorstLandlord = owner.latestWorstLandlordsYear != null;
  const buildingCount = typeof owner.numBuildings === 'number' ? owner.numBuildings : null;
  const name = owner.name || 'Owner';
  const avatarLabel = name.trim().charAt(0).toUpperCase();

  const handleClick = () => {
    if (owner.id) {
      router.push(contactUrl(owner));
    }
  };

  const handleTagClick = (tag: 'topRated' | 'worstLandlord') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTag(tag);
    setShowTagDetails(true);
  };

  const handleCloseTagDetails = () => {
    setShowTagDetails(false);
    setActiveTag(null);
  };

  const tagAnchorRef =
    activeTag === 'topRated'
      ? topRatedTagRef
      : activeTag === 'worstLandlord'
        ? worstLandlordTagRef
        : undefined;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 w-full shadow p-4 rounded-2xl ',
        'border border-secondary hover:bg-primary-hover bg-primary',
        'cursor-pointer transition-colors duration-200',
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      {/* Top container: Avatar left, Tag right */}
      <div className="flex items-start justify-between w-full">
        <Avatar type="Owner" label={avatarLabel} size={44} />
        {isOwnerTopRated || isWorstLandlord ? (
          <div className="flex items-center gap-2">
            {isOwnerTopRated ? (
              <div ref={topRatedTagRef}>
                <Tag
                  className="!shadow"
                  opaque
                  variant="topRated"
                  onClick={handleTagClick('topRated')}
                />
              </div>
            ) : null}
            {isWorstLandlord ? (
              <div ref={worstLandlordTagRef}>
                <Tag
                  className="!shadow"
                  opaque
                  variant="worstLandlord"
                  onClick={handleTagClick('worstLandlord')}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Content container */}
      <div className="flex flex-col gap-1 w-full pt-2">
        {/* Name - Body 2 High emphasis */}
        <h3 className="text-body-2-high text-primary truncate w-full overflow-ellipsis overflow-hidden whitespace-nowrap">
          {titleCase(name)}
        </h3>

        {/* Lower container: Rating · Buildings */}
        <div className="flex items-start gap-1 w-full">
          {hasReviews && (
            <Rating value={owner.averageRating as number} count={owner.reviewCount as number} />
          )}
          {hasReviews && typeof buildingCount === 'number' && (
            <span className="text-body-2 text-secondary overflow-ellipsis overflow-hidden whitespace-nowrap">
              ·
            </span>
          )}
          {typeof buildingCount === 'number' ? (
            <p className="text-body-2 text-secondary overflow-ellipsis overflow-hidden whitespace-nowrap">
              {buildingCount} building{buildingCount === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
      </div>

      {/* Tag Details Modal/Bottom Sheet */}
      {activeTag && (
        <>
          {isMobile ? (
            <BottomSheet
              open={showTagDetails}
              onOpenChange={isOpen => {
                setShowTagDetails(isOpen);
                if (!isOpen) setActiveTag(null);
              }}
              label={'Owner highlights'}
              onClose={handleCloseTagDetails}
              bodyClassName="!px-4 !py-2"
            >
              {activeTag === 'topRated' ? (
                <OwnerHighlightsContent />
              ) : (
                <WorstLandlordHighlightsContent />
              )}
            </BottomSheet>
          ) : (
            <InfoPopover
              open={showTagDetails}
              onOpenChange={isOpen => !isOpen && handleCloseTagDetails()}
              onClose={handleCloseTagDetails}
              label={'Owner highlights'}
              {...(tagAnchorRef && { anchorRef: tagAnchorRef })}
              placement="below"
              align="center"
              offsetY={8}
              bodyClassName="px-4 py-2"
            >
              {activeTag === 'topRated' ? (
                <OwnerHighlightsContent />
              ) : (
                <WorstLandlordHighlightsContent />
              )}
            </InfoPopover>
          )}
        </>
      )}
    </div>
  );
}
